// ── Backend Selector ────────────────────────────────────────────────────────
// Auto-detects the best available AI backend in priority order:
//   1. Ollama (most capable, requires separate install)
//   2. WebLLM (best in-browser option, requires WebGPU)
//   3. Gemini Nano (Chrome built-in fallback)

import type { AIBackend, AIBackendType, AIBackendStatus } from '../types';
import { OllamaBackend } from './OllamaBackend';
import { WebLLMBackend } from './WebLLMBackend';
import { GeminiNanoBackend } from './GeminiNanoBackend';

export interface BackendProbeResult {
  type: AIBackendType;
  status: AIBackendStatus;
  backend: AIBackend | null;
}

/** Probe all backends and return their statuses. */
export async function probeAllBackends(): Promise<BackendProbeResult[]> {
  const candidates: AIBackend[] = [
    new OllamaBackend(),
    new WebLLMBackend(),
    new GeminiNanoBackend(),
  ];

  const results: BackendProbeResult[] = [];
  for (const backend of candidates) {
    try {
      const status = await backend.checkAvailability();
      results.push({ type: backend.type, status, backend: status !== 'unavailable' ? backend : null });
    } catch {
      results.push({ type: backend.type, status: 'unavailable', backend: null });
    }
  }
  return results;
}

/** Select the best available backend. Returns null if none available. */
export async function selectBestBackend(): Promise<AIBackend | null> {
  const results = await probeAllBackends();

  // Prefer ready backends first
  const ready = results.find(r => r.status === 'ready');
  if (ready?.backend) return ready.backend;

  // Then downloadable (model needs download but backend is supported)
  const downloadable = results.find(r => r.status === 'downloadable');
  if (downloadable?.backend) return downloadable.backend;

  return null;
}

/** Create a specific backend by type. */
export function createBackend(type: AIBackendType): AIBackend {
  switch (type) {
    case 'ollama': return new OllamaBackend();
    case 'webllm': return new WebLLMBackend();
    case 'gemini-nano': return new GeminiNanoBackend();
    default: throw new Error(`Unknown backend type: ${type}`);
  }
}
